import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import campService from '../modules/Camp/services/campService';

const COMMON_API = '/ykrestapi/CommonChannel';

const TILES = [
  {
    key: 'Camp',
    label: 'Camp',
    icon: '\uD83C\uDFD5\uFE0F',
    accent: '#f97316',
    accentLight: '#fff7ed',
    borderActive: '#f97316',
    shadow: '0 8px 30px rgba(249,115,22,0.18)',
  },
  {
    key: 'Class',
    label: 'Classes',
    icon: '\uD83C\uDF93',
    accent: '#6366f1',
    accentLight: '#eef2ff',
    borderActive: '#6366f1',
    shadow: '0 8px 30px rgba(99,102,241,0.18)',
  },
  {
    key: 'Tiffin',
    label: 'Tiffin',
    icon: '\uD83C\uDF71',
    accent: '#10b981',
    accentLight: '#ecfdf5',
    borderActive: '#10b981',
    shadow: '0 8px 30px rgba(16,185,129,0.18)',
  },
];

const CAMP_COLS  = ['#', 'Camp / Event', 'Student', 'Weeks', 'Mode', 'Paid Amt', 'Status', 'Date', 'Receipt'];
const CLASS_COLS = ['#', 'Class Name', 'Mode', 'Gross Amt', 'Net Amt', 'Status', 'Date'];
const TIFFIN_COLS= ['#', 'Order #', 'Mode', 'Order Total', 'Item Amt', 'Status', 'Date'];

const SORT_KEYS = {
  Camp:   ['', 'sourceName', 'studentNames', 'weeksRegistered', 'mode', 'paid_Amt', 'status', 'created', ''],
  Class:  ['', 'sourceName', 'mode', 'total_Gross_Amt', 'net_Amt', 'status', 'created'],
  Tiffin: ['', 'payment_Id', 'mode', 'total_Gross_Amt', 'itemAmt', 'status', 'created'],
};

function formatDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtAmt(val) {
  if (val == null || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  const cls =
    s === 'paid'    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
    s === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
    s === 'failure' ? 'bg-red-50 text-red-600 border border-red-200' :
                      'bg-gray-100 text-gray-500 border border-gray-200';
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${cls}`}>
      {status || '—'}
    </span>
  );
}

const BTN_CLS = 'inline-flex items-center gap-1.5 text-xs font-bold text-gray-800 bg-white border border-gray-200 px-3 py-1.5 rounded-lg transition-all select-none';
const BTN_STYLE = { boxShadow: '0 3px 0 0 #cbd5e1, 0 2px 6px rgba(0,0,0,0.08)' };
const onEnter = e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 2px 0 0 #cbd5e1'; };
const onLeave = e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = BTN_STYLE.boxShadow; };
const onDown  = e => { e.currentTarget.style.transform = 'translateY(3px)'; e.currentTarget.style.boxShadow = 'none'; };
const onUp    = e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 2px 0 0 #cbd5e1'; };

function ReceiptCell({ row }) {
  const [url, setUrl] = useState(row.receipt_Url || '');
  const [loading, setLoading] = useState(false);
  const user = useSelector(state => state.auth.user);

  const isPaid = row.status?.toLowerCase() === 'paid';
  if (!isPaid) return <span className="text-gray-300 text-xs">—</span>;

  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className={BTN_CLS} style={BTN_STYLE}
        onMouseEnter={onEnter} onMouseLeave={onLeave} onMouseDown={onDown} onMouseUp={onUp}>
        🧾 View Receipt
      </a>
    );
  }

  const handleViewReceipt = async () => {
    const win = window.open('', '_blank');
    const loadingHtml =
      '<html><head><title>Receipt</title></head><body style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#f9fafb;gap:12px;">' +
      '<div style="font-size:48px;">⏳</div>' +
      '<p style="font-weight:700;color:#374151;font-size:16px;margin:0;">Loading your receipt…</p>' +
      '<p style="color:#9ca3af;font-size:13px;margin:0;">Please wait a moment</p>' +
      '</body></html>';
    const errorHtml = (msg) =>
      '<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;flex-direction:column;gap:12px;background:#fef2f2;">' +
      '<div style="font-size:40px;">⚠️</div>' +
      `<p style="font-weight:700;color:#b91c1c;">${msg}</p>` +
      '<p style="color:#6b7280;font-size:13px;">Contact campregistration@yugalkunj.org</p>' +
      '</body></html>';
    try { win.document.write(loadingHtml); } catch {}
    setLoading(true);
    try {
      // Step 1: fetch order data — returns stored Receipt_Url if already generated
      const data = await campService.getCampOrderForReceipt(row.payment_Id);
      const storedUrl = data?.receipt_Url || data?.Receipt_Url || '';
      if (storedUrl) {
        // Fast path: URL already in DB — no email sent
        win.location.href = storedUrl;
        setUrl(storedUrl);
        return;
      }
      // Step 2: No stored URL — generate receipt PDF (email sent to user, first time only)
      const emailResp = await campService.sendCampConfirmationEmail({
        toEmail:        data?.toEmail        || data?.ToEmail        || '',
        userName:       data?.userName       || data?.UserName       || '',
        campName:       data?.campName       || data?.CampName       || '',
        userPhone:      data?.userPhone      || data?.UserPhone      || '',
        registrationId: data?.registrationId || data?.RegistrationId || row.payment_Id || '',
        amount:   String(data?.amount        || data?.Amount         || 0),
        paymentType:    data?.paymentType    || data?.PaymentType    || 'CC',
        transactionId:  data?.transactionId  || data?.TransactionId  || row.receipt_Id || '',
        campDateRange:  data?.campDateRange  || data?.CampDateRange  || '',
        studentName:    data?.studentNames   || data?.StudentNames   || '',
        userFirstName:  data?.userFirstName  || data?.UserFirstName  || '',
        userLastName:   data?.userLastName   || data?.UserLastName   || '',
        students: [],
      });
      const newUrl =
        (typeof emailResp === 'string' ? emailResp : '') ||
        emailResp?.receiptUrl || emailResp?.ReceiptUrl || emailResp?.attachmentUrl ||
        emailResp?.data?.receiptUrl || emailResp?.data?.attachmentUrl || '';
      if (newUrl) {
        win.location.href = newUrl;
        setUrl(newUrl);
        // Persist URL so future clicks skip the email step entirely
        campService.updateCampReceiptUrl(row.payment_Id, newUrl).catch(() => {});
      } else {
        try { win.document.write(errorHtml('Receipt could not be generated.')); } catch {}
      }
    } catch (e) {
      console.error('Receipt error:', e);
      try { win.document.write(errorHtml('Error loading receipt.')); } catch {}
    } finally {
      setLoading(false);
    }
  };

  return (
    <button onClick={handleViewReceipt} disabled={loading}
      className={BTN_CLS + ' disabled:opacity-50 disabled:cursor-not-allowed'}
      style={loading ? { boxShadow: 'none', opacity: 0.6 } : BTN_STYLE}
      onMouseEnter={e => { if (!loading) onEnter(e); }}
      onMouseLeave={e => { if (!loading) onLeave(e); }}
      onMouseDown={e  => { if (!loading) onDown(e);  }}
      onMouseUp={e    => { if (!loading) onUp(e);    }}
    >
      {loading ? '⏳ Loading…' : '🧾 View Receipt'}
    </button>
  );
}

function SortIcon({ dir }) {
  if (!dir) return <span className="ml-1 text-gray-300 text-xs">⇅</span>;
  return <span className="ml-1 text-xs">{dir === 'asc' ? '↑' : '↓'}</span>;
}

const MyOrders = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userId = parseInt(user?.user_Id || user?.User_Id || user?.userId || user?.id || 0, 10);

  const [activeTile, setActiveTile] = useState(null);
  const [summaryData, setSummaryData] = useState({ Camp: [], Class: [], Tiffin: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const fetchAllSummary = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const [camp, cls, tiffin] = await Promise.all([
        apiService.post(`${COMMON_API}/GetOrderHistory`, { iUser_Id: userId, sFlag: 'Camp' }),
        apiService.post(`${COMMON_API}/GetOrderHistory`, { iUser_Id: userId, sFlag: 'Class' }),
        apiService.post(`${COMMON_API}/GetOrderHistory`, { iUser_Id: userId, sFlag: 'Tiffin' }),
      ]);
      setSummaryData({
        Camp:   camp.data   || [],
        Class:  cls.data    || [],
        Tiffin: tiffin.data || [],
      });
    } catch {
      setError('Could not load order summary. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchAllSummary(); }, [fetchAllSummary]);

  const handleTileClick = useCallback((tile) => {
    setSearch('');
    setSortCol('');
    setSortDir('asc');
    if (activeTile?.key === tile.key) { setActiveTile(null); return; }
    setActiveTile(tile);
  }, [activeTile]);

  const getSummary = useCallback((key) => {
    const rows = summaryData[key] || [];
    const total = rows.reduce((s, r) => s + parseFloat(r.paid_Amt ?? r.net_Amt ?? r.total_Gross_Amt ?? 0), 0);
    return { count: rows.length, total };
  }, [summaryData]);

  const handleSort = (colKey) => {
    if (!colKey) return;
    if (sortCol === colKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(colKey); setSortDir('asc'); }
  };

  const baseRows = activeTile ? (summaryData[activeTile.key] || []) : [];

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    let rows = q
      ? baseRows.filter(r =>
          Object.values(r).some(v => v != null && String(v).toLowerCase().includes(q))
        )
      : baseRows;
    if (sortCol) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortCol] ?? ''; const bv = b[sortCol] ?? '';
        const an = parseFloat(av); const bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [baseRows, search, sortCol, sortDir]);

  const paidTotal = useMemo(() =>
    filteredRows.reduce((s, r) => s + parseFloat(r.paid_Amt ?? r.net_Amt ?? 0), 0),
  [filteredRows]);

  const cols = activeTile ? (
    activeTile.key === 'Camp' ? CAMP_COLS : activeTile.key === 'Class' ? CLASS_COLS : TIFFIN_COLS
  ) : [];
  const sortKeys = activeTile ? (SORT_KEYS[activeTile.key] || []) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-700">My Orders</h1>
            <p className="text-sm text-gray-500 mt-0.5">All your registrations and payments in one place</p>
          </div>
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-full border-2 border-gray-200 shadow-sm transition text-sm">
            ← Back
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex gap-2 items-center">
            <span>⚠️</span> {error}
            <button onClick={fetchAllSummary} className="ml-auto text-red-600 underline text-xs">Retry</button>
          </div>
        )}

        {/* Tiles */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
            {TILES.map((tile) => {
              const { count, total } = getSummary(tile.key);
              const isActive = activeTile?.key === tile.key;
              return (
                <button
                  key={tile.key}
                  onClick={() => handleTileClick(tile)}
                  style={{
                    background: '#ffffff',
                    borderRadius: 20,
                    border: isActive ? `2px solid ${tile.accent}` : '2px solid transparent',
                    boxShadow: isActive
                      ? `0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08), 0 0 0 1px ${tile.accent}22`
                      : '0 4px 14px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)',
                    transform: isActive ? 'translateY(-3px)' : 'translateY(0)',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                  }}
                  className="relative text-left focus:outline-none w-full"
                  title={`View ${tile.label} orders`}
                >
                  {/* Thin top accent bar */}
                  <div style={{ height: 3, background: tile.accent, opacity: isActive ? 1 : 0.6 }} />

                  <div className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl"
                          style={{ background: '#f8fafc', boxShadow: '0 2px 6px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,1)' }}>
                          {tile.icon}
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 text-base">{tile.label}</p>
                          <p className="text-xs text-gray-400">{count} order{count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      {isActive && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                          style={{ background: tile.accent }}>
                          Active
                        </span>
                      )}
                    </div>

                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Total Paid</p>
                        <p className="text-2xl font-extrabold text-gray-900">{fmtAmt(total)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400 mb-0.5">Orders</p>
                        <p className="text-2xl font-extrabold" style={{ color: tile.accent }}>{count}</p>
                      </div>
                    </div>
                  </div>

                  <div className="px-5 py-2.5 text-xs font-medium text-gray-400"
                    style={{ borderTop: '1px solid #f1f5f9', background: '#fafafa' }}>
                    {count === 0 ? 'No orders found' : 'Tap to view details →'}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Mobile-only backdrop */}
        {activeTile && <div className="sm:hidden fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }} />}

        {/* Orders panel — modal on mobile, inline on desktop */}
        {activeTile && (
          <div className="fixed inset-0 z-50 flex flex-col bg-white sm:static sm:inset-auto sm:z-auto sm:flex-none sm:rounded-2xl sm:border sm:border-gray-100 sm:overflow-hidden sm:mt-2"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)' }}>

            {/* Modal toolbar — clean white 3D */}
            <div className="px-4 py-3 flex gap-3 items-center justify-between flex-shrink-0"
              style={{
                background: 'linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)',
                borderBottom: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{ background: '#f1f5f9', boxShadow: '0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)' }}>
                  {activeTile.icon}
                </div>
                <div className="min-w-0">
                  <h2 className="font-extrabold text-gray-900 text-base leading-tight tracking-tight">{activeTile.label} Orders</h2>
                  <p className="text-xs text-gray-400 font-medium">
                    {filteredRows.length} of {baseRows.length} orders
                  </p>
                </div>
              </div>
              {/* Close btn — desktop inline */}
              <button onClick={() => { setActiveTile(null); setSearch(''); }}
                className="hidden sm:flex w-8 h-8 items-center justify-center rounded-xl font-bold text-sm flex-shrink-0"
                style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#6b7280', boxShadow: '0 2px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1)' }}>
                ✕
              </button>
            </div>

            {/* Search bar row — full width on mobile */}
            <div className="px-3 py-2.5 flex-shrink-0"
              style={{ background: 'linear-gradient(180deg,#f8fafc 0%,#f1f5f9 100%)', borderBottom: '1px solid #e5e7eb' }}>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search orders…"
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-2xl focus:outline-none"
                    style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.06), inset 0 1px 2px rgba(0,0,0,0.04)', fontSize: 15 }}
                  />
                  <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {search && (
                    <button onClick={() => setSearch('')}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-700 text-xs font-bold">✕</button>
                  )}
                </div>
                {/* Close btn — mobile only */}
                <button onClick={() => { setActiveTile(null); setSearch(''); }}
                  className="sm:hidden flex-shrink-0 flex items-center justify-center rounded-2xl font-bold text-sm px-4 py-2.5"
                  style={{ background: '#1e293b', color: '#fff', boxShadow: '0 4px 10px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.08)' }}>
                  Close
                </button>
              </div>
            </div>

            {baseRows.length === 0 ? (
              <div className="text-center py-16">
                <span className="text-5xl mb-4 block">{activeTile.icon}</span>
                <p className="font-semibold text-gray-500">No orders found.</p>
                <p className="text-sm mt-1 text-gray-400">Complete a registration to see it here.</p>
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="font-semibold">No results for &ldquo;{search}&rdquo;</p>
                <button onClick={() => setSearch('')} className="mt-2 text-sm underline">Clear search</button>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto sm:flex-none sm:overflow-visible" style={{ background: '#f1f5f9' }}>
                {/* ── Mobile vertical card list (< sm) ── */}
                <div className="block sm:hidden px-3 py-3 space-y-3">
                  {filteredRows.map((row, idx) => {
                    const isEven = idx % 2 === 0;
                    const amt = activeTile.key === 'Camp'
                      ? fmtAmt(row.paid_Amt)
                      : activeTile.key === 'Class'
                        ? fmtAmt(row.net_Amt)
                        : fmtAmt(row.itemAmt);
                    return (
                      <div
                        key={idx}
                        style={{
                          borderRadius: 16,
                          background: '#ffffff',
                          boxShadow: isEven
                            ? '0 6px 16px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)'
                            : '0 3px 10px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.05)',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Subtle top separator */}
                        <div style={{ height: 3, background: isEven ? '#e2e8f0' : '#f1f5f9' }} />
                        <div className="p-4" style={{ minWidth: 0, overflow: 'hidden' }}>
                          {/* Top row: name + status + amount */}
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-gray-900 text-sm leading-snug truncate" title={row.sourceName}>{row.sourceName || '—'}</p>
                              <p className="text-xs text-gray-400 font-mono mt-0.5">#{row.payment_Id || '—'}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <StatusBadge status={row.status} />
                              <span className="font-black font-mono text-base text-gray-900">{amt}</span>
                            </div>
                          </div>

                          {/* Students */}
                          {activeTile.key === 'Camp' && row.studentNames && (
                            <div className="mb-2">
                              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">Students</p>
                              <div className="flex flex-wrap gap-1" style={{ minWidth: 0 }}>
                                {row.studentNames.split(',').map((n, i) => {
                                  const name = n.trim();
                                  const MAX = 18;
                                  const display = name.length > MAX ? name.slice(0, MAX) + '…' : name;
                                  return (
                                    <span key={i} title={name}
                                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
                                      style={{ maxWidth: '100%', overflow: 'hidden', background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#374151' }}>
                                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#94a3b8' }} />
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{display}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Weeks */}
                          {activeTile.key === 'Camp' && (row.weeksLabel || row.weeksRegistered > 0) && (
                            <div className="mb-2">
                              {(() => {
                                const wLabel = row.weeksLabel || `${row.weeksRegistered} week${row.weeksRegistered !== 1 ? 's' : ''}`;
                                const MAX = 28;
                                const display = wLabel.length > MAX ? wLabel.slice(0, MAX) + '…' : wLabel;
                                return (
                                  <span title={wLabel}
                                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold"
                                    style={{ maxWidth: '100%', overflow: 'hidden', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569' }}>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📅 {display}</span>
                                  </span>
                                );
                              })()}
                            </div>
                          )}

                          {/* Footer row */}
                          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <span className="text-xs text-gray-400">{formatDate(row.created)}</span>
                            {row.mode && <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 font-medium">{row.mode}</span>}
                          </div>

                          {/* Receipt */}
                          {activeTile.key === 'Camp' && (
                            <div className="mt-3">
                              <ReceiptCell row={row} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {/* Total footer */}
                  <div className="flex justify-between items-center px-1 pt-1 pb-2">
                    <span className="text-xs font-bold text-gray-400">{filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''}{search ? ` · filtered` : ''}</span>
                    <span className="font-bold font-mono text-sm text-gray-700">{fmtAmt(paidTotal)}</span>
                  </div>
                </div>

                {/* ── Desktop table (sm+) ── */}
                <div className="hidden sm:block" style={{ background: '#fff' }}>
                  <table className="w-full text-sm" style={{ tableLayout: 'auto' }}>
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        {cols.map((col, ci) => {
                          const sk = sortKeys[ci] || '';
                          const isSort = sk && sortCol === sk;
                          return (
                            <th key={ci}
                              onClick={() => handleSort(sk)}
                              className={`px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap select-none text-xs ${sk ? 'cursor-pointer hover:bg-gray-100' : ''}`}>
                              {col}
                              {sk && <SortIcon dir={isSort ? sortDir : null} />}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredRows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-3 text-gray-400 text-xs">{idx + 1}</td>
                          <td className="px-3 py-3">
                            <p className="font-semibold text-gray-800 truncate text-xs" title={row.sourceName}>{row.sourceName || '—'}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">#{row.payment_Id || '—'}</p>
                          </td>
                          {activeTile.key === 'Camp' && <>
                            <td className="px-3 py-2">
                              {row.studentNames
                                ? row.studentNames.split(',').map((n, i) => {
                                    const name = n.trim();
                                    return (
                                      <div key={i} className="flex items-center gap-1 mb-0.5" title={name}>
                                        <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                                        <span className="text-xs text-gray-700 truncate block max-w-[120px]">{name}</span>
                                      </div>
                                    );
                                  })
                                : <span className="text-gray-300 text-xs">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.weeksLabel && row.weeksLabel !== ''
                                ? <span className="text-xs font-semibold text-indigo-600 leading-snug block" title={row.weeksLabel}>{row.weeksLabel}</span>
                                : row.weeksRegistered > 0
                                  ? <span className="text-xs font-semibold text-indigo-600">{row.weeksRegistered} wk</span>
                                  : <span className="text-gray-300 text-xs">—</span>}
                            </td>
                          </>}
                          <td className="px-3 py-2 text-gray-600 text-xs truncate" title={row.mode}>{row.mode || '—'}</td>
                          {activeTile.key === 'Camp' ? (
                            <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900 text-xs">{fmtAmt(row.paid_Amt)}</td>
                          ) : activeTile.key === 'Class' ? (<>
                            <td className="px-3 py-2 text-right font-mono text-gray-600 text-xs">{fmtAmt(row.total_Gross_Amt)}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900 text-xs">{fmtAmt(row.net_Amt)}</td>
                          </>) : (<>
                            <td className="px-3 py-2 text-right font-mono text-gray-600 text-xs">{fmtAmt(row.total_Gross_Amt)}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900 text-xs">{fmtAmt(row.itemAmt)}</td>
                          </>)}
                          <td className="px-3 py-2"><StatusBadge status={row.status} /></td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap text-xs">{formatDate(row.created)}</td>
                          {activeTile.key === 'Camp' && (
                            <td className="px-3 py-2 text-center"><ReceiptCell row={row} /></td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-100">
                      <tr>
                        <td colSpan={activeTile.key === 'Camp' ? 5 : 3} className="px-4 py-3 text-xs font-bold text-gray-500">
                          {filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''}
                          {search && ` (filtered from ${baseRows.length})`}
                        </td>
                        <td className="px-4 py-3 text-right font-bold font-mono" style={{ color: activeTile.accent }}>{fmtAmt(paidTotal)}</td>
                        <td colSpan={activeTile.key === 'Camp' ? 3 : 2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
